import { SAME_ORIGIN_RELAYER_PATH } from "../../../config/relayers/resolve";

/**
 * Read a gasless-relayer response, or explain precisely why it is unreadable.
 *
 * The relayer is reached at a SAME-ORIGIN path (`/relayer`), proxied to the real
 * upstream by nginx in production and by the dev proxy locally. When that proxy
 * is missing on a deployment, the request falls through to the single-page app's
 * catch-all route, which answers `index.html` with **200 OK**.
 *
 * That combination defeats the obvious guard. `res.ok` is true, so the code
 * proceeded to `res.json()` and the user saw:
 *
 *     Unexpected token '<', "<!doctype "... is not valid JSON
 *
 * — which names neither the relayer nor the proxy, and reads like a bug in the
 * order code rather than a deployment that is missing a route. This is the
 * difference between an error someone can act on and one they cannot.
 *
 * The message deliberately offers NO workaround. An earlier version of it
 * suggested turning off One-Click Trading; that was wrong. `usePerpOrder.place`
 * enters the gasless path on `config.gasless && config.relayerUrl &&
 * gaslessPerpRouter` and returns from it — the One-Click toggle is not part of
 * that condition, so switching it off changes nothing about which path runs.
 * Suggesting a workaround that does not work is worse than admitting there
 * isn't one.
 *
 * Uses `text()` rather than `json()` because a Response body can only be read
 * once: we need the raw text both to recognise HTML and to report what came back.
 */
export const parseRelayerResponse = async (
  res: Response,
  /** What was being asked for, e.g. "nonce" or "order relay" — used in the message. */
  what: string
): Promise<any> => {
  const body = await res.text().catch(() => "");

  // The app answering instead of the relayer. Checked BEFORE `res.ok`, because
  // this specific failure arrives as a 200 and would otherwise look like success.
  if (/^\s*<(?:!doctype|html)/i.test(body)) {
    throw new Error(
      `Orders cannot be submitted on this deployment right now: the ${what} request returned this app's own page instead of a relayer response. ` +
        `The "${SAME_ORIGIN_RELAYER_PATH}" path is not proxied to the relayer. ` +
        `This is a server configuration problem, not something you can work around in the app — please report it.`
    );
  }

  if (!res.ok) {
    // A real relayer error: surface what it said, trimmed, rather than a status code alone.
    const detail = body.trim().slice(0, 200);

    // 5xx is the relayer failing, not rejecting. Calling it "rejected" sends the
    // trader hunting for a mistake in an order that was never the problem —
    // seen live as a 500 on /nonce reported as "the relayer rejected the nonce
    // request". Say whose fault it is, because that decides what they do next:
    // retry, or go and fix their order.
    if (res.status >= 500) {
      throw new Error(
        `The gasless relayer is unavailable (HTTP ${res.status}). This is a ` +
          `problem on our side, not with your order — please try again shortly.` +
          (detail ? ` Relayer said: ${detail}` : "")
      );
    }

    throw new Error(
      `The gasless relayer rejected the ${what} request (HTTP ${res.status})` +
        (detail ? `: ${detail}` : ". Please try again.")
    );
  }

  if (!body.trim()) {
    throw new Error(
      `The gasless relayer returned an empty ${what} response. Please try again.`
    );
  }

  try {
    return JSON.parse(body);
  } catch {
    throw new Error(
      `The gasless relayer returned an unreadable ${what} response. Please try again.`
    );
  }
};

/**
 * The same read, for call sites that want to inspect the payload themselves
 * rather than throw — the relay POSTs, which report their own errors from
 * fields inside the body. Still converts the "app answered instead" case into a
 * real error, because that one is never recoverable by reading the payload.
 */
export const parseRelayerResponseSoft = async (
  res: Response,
  what: string
): Promise<any> => {
  const body = await res.text().catch(() => "");

  if (/^\s*<(?:!doctype|html)/i.test(body)) {
    throw new Error(
      `Orders cannot be submitted on this deployment right now: the ${what} request returned this app's own page instead of a relayer response. ` +
        `The "${SAME_ORIGIN_RELAYER_PATH}" path is not proxied to the relayer. ` +
        `This is a server configuration problem, not something you can work around in the app — please report it.`
    );
  }

  try {
    return JSON.parse(body);
  } catch {
    return {};
  }
};
